/**
 * Redeem an emailed link (`/link?token=...`) — UI_SDD §4.0, DEC-140.
 *
 * Serves both purposes. The user never needs to know whether they were sent an
 * invitation or a return sign-in link.
 *
 * **The token is spent by the POST this page makes after it mounts, never by the
 * navigation that opened it.** Corporate mail scanners and browser link
 * prefetchers issue GET requests against links in email. If opening the URL
 * redeemed the token, those systems would consume it before the recipient
 * clicked, and the person would be shown "already used" for a link they never
 * opened. That is a routine failure of naive magic-link implementations and this
 * comment exists so it is not reintroduced.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { linkFailureReason, redeemLink } from '../api/auth'
import type { LinkFailureReason } from '../api/auth'
import { useAuth } from '../hooks/useAuth'
import { Button } from '../components/shared/Button'
import { Spinner } from '../components/shared/Spinner'
import { PageTitle } from '../components/shared/PageTitle'

type State =
  | { phase: 'verifying' }
  | { phase: 'success'; workspace: string }
  | { phase: 'failed'; reason: LinkFailureReason }

/**
 * The five failure screens (§38.7). They are never collapsed into one: "this
 * invitation was cancelled" and "this link has already been used" are different
 * facts, and a person whose access was withdrawn is entitled to the first.
 *
 * `revoked` and `member_disabled` deliberately offer no retry. Sending someone
 * through a flow that cannot succeed is worse than telling them to talk to a
 * person, which is the actual next step.
 */
const FAILURES: Record<
  LinkFailureReason,
  { heading: string; body: string; canRetry: boolean }
> = {
  expired: {
    heading: 'This link has expired',
    body: 'Sign in links are valid for a limited time. Request a new one and we will email it to you.',
    canRetry: true,
  },
  already_used: {
    heading: 'This link has already been used',
    body: 'Each link works once. Request a new one to sign in again.',
    canRetry: true,
  },
  revoked: {
    heading: 'This invitation was cancelled',
    body: 'Your workspace admin cancelled this invitation. Contact them if you think this is a mistake.',
    canRetry: false,
  },
  member_disabled: {
    heading: 'Your access has been removed',
    body: 'This account no longer has access to its workspace. Contact your workspace admin.',
    canRetry: false,
  },
  not_found: {
    heading: 'This link is not valid',
    body: 'The link may have been copied incompletely. Try opening it from your email again.',
    canRetry: true,
  },

  // ---- Google sign-in (DEC-144, SDD §38.11) --------------------------------

  // Since DEC-202 an address with no workspace is offered one, so this is reached only
  // when the address is held by a different Google account in TDIA.
  not_invited: {
    heading: 'This Google account cannot sign in here',
    body: 'This address is held by a different Google account in TDIA. Use the sign in link we can email you, or ask your workspace admin.',
    canRetry: true,
  },
  email_unverified: {
    heading: 'Google has not verified this address',
    body: 'Verify the address with Google and try again, or use the sign in link we can email you.',
    canRetry: true,
  },
  domain_not_allowed: {
    heading: 'Use your work Google account',
    body: 'This workspace accepts Google sign in from work accounts only. If you were invited on a personal address, request an email sign in link instead.',
    // The one failure whose retry is genuinely useful: the emailed link is
    // exactly what works for this person (DEC-144). Saying so is why this reason
    // exists separately rather than collapsing into not_invited.
    canRetry: true,
  },
  denied: {
    heading: 'Sign in was cancelled',
    body: 'You cancelled the Google sign in before it finished. You can try again.',
    canRetry: true,
  },
  invalid_state: {
    heading: 'This sign in expired',
    body: 'Sign in attempts are valid for a short time. Start again from the sign in page.',
    canRetry: true,
  },
  exchange_failed: {
    heading: 'Google could not be reached',
    body: 'We could not finish signing you in with Google. Try again in a moment.',
    canRetry: true,
  },

  // ---- DEC-202 / DEC-204 -----------------------------------------------------

  workspace_disabled: {
    heading: 'This workspace has been switched off',
    body: 'Athenaworks has switched this workspace off, so nobody can sign in to it. Contact Athenaworks.',
    canRetry: false,
  },
  signup_rate_limited: {
    heading: 'Too many new workspaces from here',
    body: 'Too many workspaces were requested from this network in the last hour. Try again later.',
    canRetry: true,
  },
}

export default function LinkSignInPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { refresh } = useAuth()
  const [state, setState] = useState<State>({ phase: 'verifying' })

  const token = searchParams.get('token')
  // The Google callback redirects here with a reason instead of a token
  // (§38.11), so the same five-screen component serves both flows.
  const googleError = searchParams.get('google_error')
  const next = searchParams.get('next') ?? '/scans'

  // A link is single use, so redeeming it twice fails the second time. React
  // StrictMode double-invokes effects in development, which would do exactly
  // that and show every developer "already used" on their first click.
  const attempted = useRef(false)

  useEffect(() => {
    if (attempted.current) return
    attempted.current = true

    if (googleError) {
      setState({
        phase: 'failed',
        // Unknown values fall back rather than rendering an empty screen: a
        // reason this build does not know about is still a failure.
        reason: (googleError in FAILURES
          ? googleError
          : 'invalid_state') as LinkFailureReason,
      })
      return
    }

    if (!token) {
      setState({ phase: 'failed', reason: 'not_found' })
      return
    }

    void (async () => {
      try {
        const result = await redeemLink(token)
        await refresh()
        setState({ phase: 'success', workspace: result.tenant_name })
      } catch (err) {
        setState({ phase: 'failed', reason: linkFailureReason(err) })
      }
    })()
  }, [token, googleError, refresh])

  // Brief pause on success so the workspace being joined is actually readable,
  // rather than a flash the user cannot parse.
  useEffect(() => {
    if (state.phase !== 'success') return
    const timer = setTimeout(() => navigate(next, { replace: true }), 900)
    return () => clearTimeout(timer)
  }, [state, navigate, next])

  return (
    <div className="min-h-screen bg-ground flex flex-col">
      <header className="h-12 border-b border-hairline bg-panel flex items-center px-6">
        <span className="text-[15px] font-semibold text-ink tracking-tight">TDIA</span>
      </header>

      <main className="flex-1 flex items-start justify-center pt-24 px-4">
        <div className="w-full max-w-sm">
          {state.phase === 'verifying' && (
            <div className="flex items-center gap-3">
              <Spinner />
              <span className="text-[14px] text-muted">Signing you in...</span>
            </div>
          )}

          {state.phase === 'success' && (
            <div>
              <PageTitle size="compact">Welcome to {state.workspace}</PageTitle>
              <p className="text-[14px] text-muted mt-2">Taking you to your workspace.</p>
            </div>
          )}

          {state.phase === 'failed' && (
            <div>
              <PageTitle size="compact">{FAILURES[state.reason].heading}</PageTitle>
              <p className="text-[14px] text-muted mt-2 leading-relaxed">
                {FAILURES[state.reason].body}
              </p>
              {FAILURES[state.reason].canRetry && (
                <Button
                  className="mt-6 justify-center py-2.5"
                  onClick={() => navigate('/login', { replace: true })}
                >
                  Request a new link
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
